'use client';

import { getHubImage } from '@/lib/hub';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import NextImage from 'next/image';
import { useEffect, useMemo, useState } from 'react';

export default function HubMeetupShowcaseMedia({
  games = [],
  fallbackImage,
  fallbackAlt,
  monthLabel,
  dayLabel,
}) {
  const reducedMotion = useReducedMotion();
  const frames = useMemo(() => {
    const uniqueFrames = [];
    const seen = new Set();

    for (const game of games) {
      const src = getHubImage(game);
      if (!src || seen.has(src)) {
        continue;
      }

      seen.add(src);
      uniqueFrames.push({
        src,
        alt: game?.name || game?.gameNumber || fallbackAlt,
      });
    }

    if (!uniqueFrames.length && fallbackImage) {
      uniqueFrames.push({
        src: fallbackImage,
        alt: fallbackAlt,
      });
    }

    return uniqueFrames;
  }, [fallbackAlt, fallbackImage, games]);
  const frameKey = frames.map((frame) => frame.src).join('|');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [frameKey]);

  useEffect(() => {
    if (reducedMotion || frames.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % frames.length);
    }, 8600);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [frames.length, reducedMotion]);

  const activeFrame = frames[activeIndex] || null;

  return (
    <div className="hub-meetup-showcase__media">
      <div className="hub-meetup-showcase__media-rotator">
        {activeFrame ? (
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={`${activeIndex}-${activeFrame.src}`}
              className="hub-meetup-showcase__media-frame"
              initial={reducedMotion ? false : { opacity: 0.2, scale: 1.01 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reducedMotion ? {} : { opacity: 0.12, scale: 0.995 }}
              transition={{
                duration: reducedMotion ? 0 : 0.4,
                ease: 'easeOut',
              }}
            >
              <NextImage
                src={activeFrame.src}
                alt={activeFrame.alt || fallbackAlt}
                fill
                sizes="(min-width: 992px) 42vw, 100vw"
                className="hub-meetup-showcase__image"
                style={{ objectFit: 'cover' }}
              />
            </motion.div>
          </AnimatePresence>
        ) : null}
      </div>

      <div className="hub-meetup-showcase__media-overlay" aria-hidden="true" />

      <div className="hub-meetup-showcase__date-badge">
        <span className="hub-meetup-showcase__date-month">{monthLabel}</span>
        <strong className="hub-meetup-showcase__date-day">{dayLabel}</strong>
      </div>

      {frames.length > 1 ? (
        <div className="hub-meetup-showcase__media-pager" aria-hidden="true">
          {frames.map((frame, index) => (
            <span
              key={`${frame.src}-${index}`}
              className={`hub-meetup-showcase__media-dot${
                index === activeIndex ? ' is-active' : ''
              }`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
